// DeliveryScheduleDataGenerator.js

// Constants for Delivery Schedule
const PROJECT_NAMES = [
  'Dubai Metro Extension',
  'Al Reem Island Development',
  'Abu Dhabi Airport Expansion',
  'Qatar World Cup Stadiums',
  'Riyadh Metro Project',
  'Jeddah Tower Construction',
  'Muscat Waterfront Development',
  'Bahrain Bay Project',
  'Kuwait City Tower',
  'Sharjah Industrial Zone',
];

const SUPERVISORS = [
  'Ahmed Mohammed',
  'Khalid Hassan',
  'Omar Abdullah',
  'Yousef Ibrahim',
  'Rashid Saeed',
  'Abdul Rahman',
  'Mohammed Ali',
  'Salem Khalifa',
  'Hamad Nasser',
  'Faisal Ahmed',
];

const AREA_LOCATIONS = [
  'Dubai Marina',
  'Abu Dhabi Corniche',
  'Doha West Bay',
  'Riyadh Business District',
  'Jeddah Corniche',
  'Muscat Seeb',
  'Manama Diplomatic Area',
  'Kuwait City Salmiya',
  'Sharjah Industrial Area',
  'Al Ain Oasis',
];

const AUCTION_NAMES = [
  'Heavy Equipment Auction',
  'Construction Machinery Sale',
  'Government Surplus Auction',
  'Infrastructure Equipment Auction',
  'Building Materials Auction',
  'Industrial Plant Auction',
  'Logistics Equipment Sale',
  'Transportation Assets Auction',
  'Municipal Equipment Auction',
  'Project Completion Sale',
];

const BUYER_NAMES = [
  'Al-Futtaim Group',
  'Arabtec Construction',
  'Consolidated Contractors',
  'Drake & Scull International',
  'Nesma & Partners',
  'Saudi Binladin Group',
  'Qatar Building Company',
  'Kharafi National',
  'Al Jaber Group',
  'Al Naboodah Construction',
];

const MATERIAL_TYPES = [
  'Construction Waste',
  'Demolition Debris',
  'Excavation Soil',
  'Concrete Rubble',
  'Asphalt',
  'Gravel',
  'Sand',
  'Clay',
  'Rock',
  'Mixed Construction Material',
];

const LOAD_LOCATIONS = [
  'Construction Site A',
  'Demolition Site B',
  'Excavation Zone C',
  'Building Demolition Area',
  'Infrastructure Project Site',
  'Municipal Works Area',
  'Industrial Demolition Site',
  'Commercial Development Site',
  'Residential Construction Zone',
  'Public Works Project Area',
];

const DESTINATION_LOCATIONS = [
  'Al Warsan Landfill',
  'Jebel Ali Recycling Center',
  'Al Dhafra Disposal Facility',
  'Doha North Landfill',
  'Riyadh Recycling Plant',
  'Jeddah Waste Management',
  'Muscat Disposal Site',
  'Manama Landfill',
  'Kuwait Recycling Center',
  'Sharjah Waste Facility',
];

const VEHICLE_TYPES = [
  'Ford Truck',
  'JCB Excavator',
  'Caterpillar Dump Truck',
  'Komatsu Loader',
  'Volvo Truck',
  'Mercedes Mixer',
  'Scania Trailer',
  'MAN Tipper',
  'Tata Dumper',
  'Isuzu Truck',
];

const VEHICLE_STATUSES = {
  SCHEDULED: 'scheduled',
  IN_TRANSIT: 'in_transit',
  COMPLETED: 'completed',
  DELAYED: 'delayed',
  CANCELLED: 'cancelled',
  PENDING: 'pending',
};

const STATUS_LABELS = {
  [VEHICLE_STATUSES.SCHEDULED]: 'Scheduled',
  [VEHICLE_STATUSES.IN_TRANSIT]: 'In Transit',
  [VEHICLE_STATUSES.COMPLETED]: 'Completed',
  [VEHICLE_STATUSES.DELAYED]: 'Delayed',
  [VEHICLE_STATUSES.CANCELLED]: 'Cancelled',
  [VEHICLE_STATUSES.PENDING]: 'Pending',
};

const STATUS_COLORS = {
  [VEHICLE_STATUSES.SCHEDULED]: '#3b82f6', // blue
  [VEHICLE_STATUSES.IN_TRANSIT]: '#8b5cf6', // violet
  [VEHICLE_STATUSES.COMPLETED]: '#10b981', // green
  [VEHICLE_STATUSES.DELAYED]: '#f59e0b', // amber
  [VEHICLE_STATUSES.CANCELLED]: '#ef4444', // red
  [VEHICLE_STATUSES.PENDING]: '#6b7280', // gray
};

// Helper functions
const GENERATE_VEHICLE_NUMBER = () => {
  const emirates = ['DXB', 'AUH', 'SHJ', 'AJM', 'RAK', 'FUJ'];
  const emirate = emirates[Math.floor(Math.random() * emirates.length)];
  const number = Math.floor(1000 + Math.random() * 9000);
  const letters = String.fromCharCode(65 + Math.floor(Math.random() * 26)) + 
                  String.fromCharCode(65 + Math.floor(Math.random() * 26));
  return `${emirate} ${number} ${letters}`;
};

const RANDOM_DATE = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const randomTime = startDate.getTime() + Math.random() * (endDate.getTime() - startDate.getTime());
  return new Date(randomTime);
};

const RANDOM_TIME = () => {
  const hours = Math.floor(Math.random() * 12) + 6; // 6 AM to 6 PM
  const minutes = Math.floor(Math.random() * 60);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Generate dummy delivery schedules
export const GENERATE_DUMMY_DELIVERY_SCHEDULES = (count = 50) => {
  const schedules = [];
  
  for (let i = 1; i <= count; i++) {
    const projectName = PROJECT_NAMES[Math.floor(Math.random() * PROJECT_NAMES.length)];
    const supervisor = SUPERVISORS[Math.floor(Math.random() * SUPERVISORS.length)];
    const area = AREA_LOCATIONS[Math.floor(Math.random() * AREA_LOCATIONS.length)];
    
    // Generate assigned vehicles (1-3 vehicles per schedule)
    const numVehicles = Math.floor(Math.random() * 3) + 1;
    const assignedVehicles = [];
    const totalExpectedWeight = Math.random() * 100 + 20; // 20-120 tons
    
    for (let v = 0; v < numVehicles; v++) {
      const vehicleType = VEHICLE_TYPES[Math.floor(Math.random() * VEHICLE_TYPES.length)];
      const vehicleNumber = GENERATE_VEHICLE_NUMBER();
      const vehicleWeight = totalExpectedWeight / numVehicles + (Math.random() * 10 - 5); // Distribute weight
      
      assignedVehicles.push({
        id: v + 1,
        name: vehicleType,
        number: vehicleNumber,
        code: `VEH-${Math.floor(1000 + Math.random() * 9000)}`,
        capacity: `${vehicleWeight.toFixed(1)} tons`,
        driver: SUPERVISORS[Math.floor(Math.random() * SUPERVISORS.length)],
      });
    }
    
    // Generate auction sites (1-3 auctions per schedule)
    const numAuctions = Math.floor(Math.random() * 3) + 1;
    const auctionSites = [];
    let auctionTotalWeight = 0;
    
    for (let a = 0; a < numAuctions; a++) {
      const auctionWeight = Math.random() * 50 + 10; // 10-60 tons per auction
      auctionTotalWeight += auctionWeight;
      
      auctionSites.push({
        id: a + 1,
        auctionName: AUCTION_NAMES[Math.floor(Math.random() * AUCTION_NAMES.length)],
        buyerName: BUYER_NAMES[Math.floor(Math.random() * BUYER_NAMES.length)],
        lotRef: `LOT-${Math.floor(10000 + Math.random() * 90000)}`,
        materialType: MATERIAL_TYPES[Math.floor(Math.random() * MATERIAL_TYPES.length)],
        expectedWeight: auctionWeight.toFixed(2),
        loadFrom: LOAD_LOCATIONS[Math.floor(Math.random() * LOAD_LOCATIONS.length)],
        loadTo: DESTINATION_LOCATIONS[Math.floor(Math.random() * DESTINATION_LOCATIONS.length)],
        expectingStartDate: RANDOM_DATE('2024-01-01', '2024-12-31').toISOString().split('T')[0],
        expectingStartTime: RANDOM_TIME(),
        status: ['pending', 'scheduled', 'completed'][Math.floor(Math.random() * 3)],
      });
    }
    
    // Generate schedule dates
    const scheduleDate = RANDOM_DATE('2024-01-01', '2024-12-31');
    const startTime = RANDOM_TIME();
    const endTime = `${parseInt(startTime.split(':')[0]) + Math.floor(Math.random() * 6) + 1}:${Math.floor(Math.random() * 60).toString().padStart(2, '0')}`;
    
    // Determine overall status
    const statusKeys = Object.keys(VEHICLE_STATUSES);
    const status = statusKeys[Math.floor(Math.random() * statusKeys.length)];
    
    // Create schedule object
    const schedule = {
      id: i,
      scheduleNumber: `SCH-${new Date().getFullYear()}-${String(i).padStart(5, '0')}`,
      projectName,
      supervisor,
      area,
      mainSite: {
        name: projectName,
        supervisor: supervisor,
        area: area,
        contactPhone: `+971 5${Math.floor(Math.random() * 10)} ${Math.floor(1000000 + Math.random() * 9000000)}`,
      },
      assignedVehicles,
      auctionSites,
      scheduleDate: scheduleDate.toISOString().split('T')[0],
      startTime,
      endTime,
      totalAuctions: numAuctions,
      totalVehicles: numVehicles,
      totalExpectedWeight: auctionTotalWeight.toFixed(2),
      actualWeight: (auctionTotalWeight * (0.8 + Math.random() * 0.4)).toFixed(2), // 80-120% of expected
      status,
      priority: ['High', 'Medium', 'Low'][Math.floor(Math.random() * 3)],
      notes: `Schedule for ${projectName}. ${numVehicles} vehicles assigned for ${numAuctions} auctions.`,
      createdBy: SUPERVISORS[Math.floor(Math.random() * SUPERVISORS.length)],
      createdAt: RANDOM_DATE('2023-01-01', scheduleDate).toISOString(),
      updatedAt: new Date().toISOString(),
      isActive: status !== 'cancelled',
    };
    
    schedules.push(schedule);
  }
  
  return schedules;
};

// Generate status summary
export const GENERATE_STATUS_SUMMARY = (schedules) => {
  const summary = {
    all: schedules.length,
    scheduled: schedules.filter(s => s.status === VEHICLE_STATUSES.SCHEDULED).length,
    in_transit: schedules.filter(s => s.status === VEHICLE_STATUSES.IN_TRANSIT).length,
    completed: schedules.filter(s => s.status === VEHICLE_STATUSES.COMPLETED).length,
    delayed: schedules.filter(s => s.status === VEHICLE_STATUSES.DELAYED).length,
    cancelled: schedules.filter(s => s.status === VEHICLE_STATUSES.CANCELLED).length,
    pending: schedules.filter(s => s.status === VEHICLE_STATUSES.PENDING).length,
  };
  
  return summary;
};

// Status badge component
export const DeliveryStatusBadge = ({ status }) => {
  const color = STATUS_COLORS[status] || '#6b7280';
  const text = STATUS_LABELS[status] || 'Unknown';
  
  return (
    <span
      style={{
        backgroundColor: `${color}20`,
        color: color,
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '500',
        letterSpacing: '0.025em',
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
      }}
    >
      <span
        style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          backgroundColor: color,
        }}
      />
      {text}
    </span>
  );
};

// Priority badge component
export const PriorityBadge = ({ priority }) => {
  const colors = {
    High: { bg: '#fee2e2', text: '#dc2626' },
    Medium: { bg: '#fef3c7', text: '#d97706' },
    Low: { bg: '#dbeafe', text: '#1e40af' },
  };
  
  const color = colors[priority] || colors.Medium;
  
  return (
    <span
      style={{
        backgroundColor: color.bg,
        color: color.text,
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: '600',
        letterSpacing: '0.025em',
      }}
    >
      {priority}
    </span>
  );
};

// Table columns for delivery schedules
export const DELIVERY_SCHEDULE_COLUMNS = [
  {
    name: '#',
    selector: (row, index) => index + 1,
    width: '60px',
    center: true,
  },
  {
    name: 'Schedule ID',
    selector: row => row.scheduleNumber,
    sortable: true,
    width: '130px',
    cell: row => (
      <div style={{ fontWeight: '600', color: '#1e40af' }}>
        {row.scheduleNumber}
      </div>
    ),
  },
  {
    name: 'Project Name',
    selector: row => row.projectName,
    sortable: true,
    width: '200px',
    cell: row => (
      <div style={{ fontWeight: '500' }}>
        {row.projectName}
      </div>
    ),
  },
  {
    name: 'Supervisor',
    selector: row => row.supervisor,
    sortable: true,
    width: '150px',
  },
  {
    name: 'Area',
    selector: row => row.area,
    sortable: true,
    width: '150px',
  },
  {
    name: 'Schedule Date',
    selector: row => row.scheduleDate,
    sortable: true,
    width: '120px',
    cell: row => (
      <div style={{ fontSize: '12px' }}>
        {new Date(row.scheduleDate).toLocaleDateString('en-GB')}
      </div>
    ),
  },
  {
    name: 'Time Slot',
    selector: row => row.startTime,
    sortable: true,
    width: '120px',
    cell: row => (
      <div style={{ fontSize: '12px', fontWeight: '500' }}>
        {row.startTime} - {row.endTime}
      </div>
    ),
  },
  {
    name: 'Total Vehicles',
    selector: row => row.totalVehicles,
    sortable: true,
    width: '100px',
    center: true,
    cell: row => (
      <div style={{ 
        backgroundColor: '#dbeafe',
        color: '#1e40af',
        padding: '2px 8px',
        borderRadius: '10px',
        fontSize: '12px',
        fontWeight: '600',
        display: 'inline-block',
      }}>
        {row.totalVehicles}
      </div>
    ),
  },
  {
    name: 'Total Auctions',
    selector: row => row.totalAuctions,
    sortable: true,
    width: '100px',
    center: true,
    cell: row => (
      <div style={{ 
        backgroundColor: '#f0f9ff',
        color: '#0369a1',
        padding: '2px 8px',
        borderRadius: '10px',
        fontSize: '12px',
        fontWeight: '600',
        display: 'inline-block',
      }}>
        {row.totalAuctions}
      </div>
    ),
  },
  {
    name: 'Weight (Kg)',
    selector: row => row.totalExpectedWeight,
    sortable: true,
    width: '120px',
    cell: row => (
      <div style={{ fontSize: '13px', fontWeight: '500' }}>
        <div>{row.totalExpectedWeight} <small style={{ color: '#6b7280' }}>expected</small></div>
        <div style={{ fontSize: '11px', color: '#059669' }}>
          {row.actualWeight} <small style={{ color: '#6b7280' }}>actual</small>
        </div>
      </div>
    ),
  },
  {
    name: 'Status',
    selector: row => row.status,
    sortable: true,
    width: '130px',
    cell: row => <DeliveryStatusBadge status={row.status} />,
  },
  {
    name: 'Priority',
    selector: row => row.priority,
    sortable: true,
    width: '90px',
    cell: row => <PriorityBadge priority={row.priority} />,
  },
  {
    name: 'Created',
    selector: row => row.createdAt,
    sortable: true,
    width: '120px',
    cell: row => (
      <div style={{ fontSize: '11px', color: '#6b7280' }}>
        {new Date(row.createdAt).toLocaleDateString('en-GB')}
      </div>
    ),
  },
];


// Mock API response
export const MOCK_DELIVERY_SCHEDULE_API_RESPONSE = (page = 1, limit = 15, filters = {}) => {
  const allSchedules = GENERATE_DUMMY_DELIVERY_SCHEDULES(100);
  const summary = GENERATE_STATUS_SUMMARY(allSchedules);
  
  // Apply filters
  let filteredSchedules = [...allSchedules];
  
  if (filters.status && filters.status !== 'all') {
    filteredSchedules = filteredSchedules.filter(s => s.status === filters.status);
  }
  
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filteredSchedules = filteredSchedules.filter(s => 
      s.scheduleNumber.toLowerCase().includes(searchLower) ||
      s.projectName.toLowerCase().includes(searchLower) ||
      s.supervisor.toLowerCase().includes(searchLower) ||
      s.area.toLowerCase().includes(searchLower)
    );
  }
  
  if (filters.priority && filters.priority !== 'all') {
    filteredSchedules = filteredSchedules.filter(s => s.priority === filters.priority);
  }
  
  // Pagination
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedSchedules = filteredSchedules.slice(startIndex, endIndex);
  
  return {
    status: true,
    data: paginatedSchedules,
    pagination: {
      total: filteredSchedules.length,
      page: page,
      limit: limit,
      pages: Math.ceil(filteredSchedules.length / limit),
    },
    meta: {
      total: summary.all,
      scheduled: summary.scheduled,
      in_transit: summary.in_transit,
      completed: summary.completed,
      delayed: summary.delayed,
      cancelled: summary.cancelled,
      pending: summary.pending,
    },
  };
};

export default {
  GENERATE_DUMMY_DELIVERY_SCHEDULES,
  GENERATE_STATUS_SUMMARY,
  DeliveryStatusBadge,
  PriorityBadge,
  DELIVERY_SCHEDULE_COLUMNS,
  MOCK_DELIVERY_SCHEDULE_API_RESPONSE,
  VEHICLE_STATUSES,
  STATUS_LABELS,
  STATUS_COLORS,
  PROJECT_NAMES,
  SUPERVISORS,
  AREA_LOCATIONS,
  AUCTION_NAMES,
  BUYER_NAMES,
  MATERIAL_TYPES,
  VEHICLE_TYPES,
};